import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import { Grid } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, get, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../controls/modals/ConfirmModal";
import { NotificationSnackBar } from "../../../../../controls/snackbars/notification-snackbar";
import { validateNoSpecialChars } from "../../../../laboratory/validates";
import { CATALOG_MATERIAL_INITIAL_VALUES } from "../../../models/CatalogMaterial";
import { CatalogOperator } from "../models/operator";
import {
  deleteCatalogOperator,
  upsertCatalogOperator,
} from "../repositories/CatalogOperatorRepository";
import { useCatalogOperatorContext } from "../context/CatalogOperatorContext";

type onModalHideProps = {
  entityId?: number;
  shouldUpdate?: boolean;
};

type Props = {
  operator: CatalogOperator | null;
  showModal: "new" | "edit" | "delete" | "";
  onHide: ({ entityId, shouldUpdate }: onModalHideProps) => void;
  refreshData: Function;
};
export const OperatorModal = ({
  operator,
  showModal,
  onHide,
  refreshData,
}: Props) => {
  const { state } = useCatalogOperatorContext();

  const [notificationMessage, setNotificationMessage] = useState("");
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [existingOperator, setExistingOperator] = useState("");
  const [title, setTitle] = useState("");

  const {
    control,
    getValues,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CatalogOperator>({
    defaultValues: CATALOG_MATERIAL_INITIAL_VALUES,
  });

  const isFormDirty =
    !(Object.keys(dirtyFields).length === 0) || showModal == "new";

  const onSubmit: SubmitHandler<CatalogOperator> = async (
    data: CatalogOperator,
  ) => {
    setIsSubmitLoading(true);
    const operators: CatalogOperator[] = state.operators || [];
    if (showModal === "delete") {
      setIsSubmitLoading(true);
      const resp = await deleteCatalogOperator(operator?.OperatorId ?? null);
      if (resp.ok) {
        await refreshData();
        onHide({ shouldUpdate: true });
        setNotificationMessage("The operator was deleted successfully");
      } else {
        setError(resp.message);
      }
      setIsSubmitLoading(false);
    } else {
      if (
        operators.some(
          (operator) =>
            operator.OperatorName === data.OperatorName &&
            operator.OperatorId !== data.OperatorId,
        )
      ) {
        setExistingOperator(data.OperatorName);
        setConfirmOpen(true);
      } else {
        await performUpsert(data);
      }
    }
  };

  const performUpsert = async (data: CatalogOperator) => {
    const resp = await upsertCatalogOperator(data);
    if (resp.ok) {
      const entityId = get(resp, "data.returnValue", 0);
      await refreshData();
      setNotificationMessage("The operator was saved successfully");
      onHide({ entityId: entityId, shouldUpdate: true });
    } else {
      setError(resp.message);
      onHide({ shouldUpdate: false });
    }
    setIsSubmitLoading(false);
  };

  useEffect(() => {
    if (showModal != "") {
      setTitle(showModal.toUpperCase());
      reset(operator || CATALOG_MATERIAL_INITIAL_VALUES);
    }
  }, [showModal]);

  return (
    <>
      <ConfirmModal
        show={confirmOpen}
        onHide={(proceed: boolean) => {
          if (proceed) {
            performUpsert(getValues());
            onHide({ shouldUpdate: true });
          }
          setConfirmOpen(false);
          setIsSubmitLoading(false);
        }}
        existingName={existingOperator}
        label="Operator"
      />
      <MesfModal
        title={`${title} OPERATOR`}
        open={showModal != ""}
        handleClose={() => onHide({ shouldUpdate: false })}
        id="operator-modal"
        maxWidth="sm"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <MesfModal.Content dividers style={{ padding: "15px 30px" }}>
            <Grid container spacing={1}>
              <Grid item xs={8} md={8}>
                <Controller
                  name="OperatorName"
                  control={control}
                  rules={{
                    required: "Name is required",
                    validate: validateNoSpecialChars,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      value={field.value}
                      onChange={field.onChange}
                      label="Name"
                      variant="outlined"
                      fullWidth
                      margin="dense"
                      autoComplete="off"
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={4}
                md={4}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Controller
                  name="Enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Enabled"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsEAF"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="EAF"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsLMF"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="LMF"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsCAS"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Caster"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsBYI"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="BYI"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsSY"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="SY"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="IsForeman"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      style={{
                        margin: 0,
                        height: "100%",
                      }}
                      control={
                        <Checkbox
                          checked={field.value}
                          color="primary"
                          onChange={field.onChange}
                        />
                      }
                      label="Foreman"
                      disabled={showModal === "delete"}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </MesfModal.Content>
          <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
            <FooterModalButtons
              isSubmitLoading={isSubmitLoading}
              onHide={() => onHide({ shouldUpdate: false })}
              buttonLabel={showModal === "delete" ? "Delete" : "Save"}
              buttonColor={showModal === "delete" ? "secondary" : "primary"}
              disabled={!isFormDirty && showModal !== "delete"}
            />
          </MesfModal.Actions>
        </form>
      </MesfModal>
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={error}
        onHide={() => setError("")}
        title="Error with Operator"
      />
    </>
  );
};
